var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { APIClient } from "@lib/api";
import { isSupportedMobileBrowser } from "@lib/browser";
import { to } from "@lib/utils";
import { AuthError } from "@models/error";
import { Network } from "@models/network";
import { User } from "@models/user";
import { WalletProvider } from "@models/wallet";
import * as Sentry from "@sentry/browser";
import { sendSignInLinkToEmail, signOut } from "firebase/auth";
import { isEmpty } from "lodash";
import { create } from "zustand";
import { firebaseAuth } from "./firebase";
import { getParticleWallet, initParticleNetwork } from "./particle";
const API_HOST = process.env.NEXT_PUBLIC_API_HOST || "";
if (!API_HOST)
    throw "Missing NEXT_PUBLIC_API_HOST";
const API_KEY = process.env.NEXT_PUBLIC_API_KEY || "";
if (!API_KEY)
    throw "Missing NEXT_PUBLIC_API_KEY";
export const apiClient = new APIClient(API_HOST, API_KEY);
export const STORAGE_AUTH_EMAIL_KEY = "moongate-auth-email";
export const STORAGE_AUTH_TOKEN_KEY = "moongate-auth-token";
export const useUserStore = create((set) => ({
    user: null,
    initialized: false,
    authenticating: false,
    fetching: false,
    idToken: "",
    authToken: "",
    sentEmailLink: false,
    firebaseAuthenticating: false,
    creatingWallet: false,
    supportedBrowser: true,
}));
export const sendSignInEmail = (email) => __awaiter(void 0, void 0, void 0, function* () {
    Sentry.addBreadcrumb({
        category: "sendSignInEmail",
        level: "info",
    });
    Sentry.setUser({
        email,
    });
    const actionCodeSettings = {
        url: `${window.location.origin}${window.location.pathname}${window.location.search || "?"}&email=${encodeURIComponent(email)}`,
        handleCodeInApp: true,
    };
    console.log("sendSignInEmail", actionCodeSettings);
    const { err } = yield to(sendSignInLinkToEmail(firebaseAuth, email, actionCodeSettings));
    if (err) {
        console.error(err.code, err.message);
        throw err.code;
    }
    useUserStore.setState({ sentEmailLink: true });
    window.localStorage.setItem(STORAGE_AUTH_EMAIL_KEY, email);
    return true;
});
const setAuthStates = (user, authToken) => {
    useUserStore.setState({
        user,
        authToken,
        fetching: false,
        authenticating: false,
        creatingWallet: false,
    });
    if (authToken)
        window.localStorage.setItem(STORAGE_AUTH_TOKEN_KEY, JSON.stringify(authToken));
    apiClient.session = (user === null || user === void 0 ? void 0 : user.session) || "";
};
export const signIn = (authId, idToken, email, address) => __awaiter(void 0, void 0, void 0, function* () {
    if (useUserStore.getState().fetching || useUserStore.getState().sentEmailLink)
        return null;
    Sentry.addBreadcrumb({
        category: "signIn",
        level: "info",
    });
    Sentry.setUser({
        authId,
        email,
        address,
    });
    useUserStore.setState({ fetching: true });
    console.log("signIn", authId, email, address);
    let user = null;
    let authToken = "";
    try {
        user = yield checkExistingUser(authId, email, address);
        console.log("checkExistingUser", user);
        if (!user || isEmpty(user.evmWallets)) {
            Sentry.addBreadcrumb({
                category: "createUserWallet",
                level: "info",
            });
            console.log("signIn", "creating new wallet...");
            useUserStore.setState({ creatingWallet: true });
            if (!isSupportedMobileBrowser()) {
                Sentry.captureMessage(AuthError.UNSUPPORTED_BROWSER);
                useUserStore.setState({ supportedBrowser: false });
                return null;
            }
            const { err: walletErr, res: newWallet } = yield to(getParticleWallet(idToken), 300);
            if (!newWallet) {
                console.error(walletErr);
                throw AuthError.CREATE_WALLET_FAILED;
            }
            Sentry.setTag("wallet", newWallet);
            ({ user, authToken } = yield fetchUserData(authId, email, newWallet, Network.EVM, WalletProvider.PARTICLE_NETWORK, window.location.origin));
            if (!user)
                throw AuthError.SAVE_WALLET_FAILED;
        }
        else {
            ({ user, authToken } = yield fetchUserData(authId, email, address));
            if (!user)
                throw AuthError.RETRIEVE_USER_DATA_FAILED;
        }
    }
    catch (error) {
        console.error("signIn", error);
        user = null;
    }
    setAuthStates(user, authToken);
    return user;
});
const checkExistingUser = (authId, email, wallet) => __awaiter(void 0, void 0, void 0, function* () {
    Sentry.addBreadcrumb({
        category: "checkExistingUser",
        level: "info",
    });
    const data = yield apiClient.get("/users", { email, wallet, authId });
    if (!(data === null || data === void 0 ? void 0 : data.id) || !(data === null || data === void 0 ? void 0 : data.identifiers))
        return null;
    const user = new User(data);
    return user;
});
const fetchUserData = (authId, email, address, network = Network.EVM, provider, source) => __awaiter(void 0, void 0, void 0, function* () {
    Sentry.addBreadcrumb({
        category: "fetchUserData",
        level: "info",
    });
    const body = {
        network,
        address,
        email,
        authId,
        provider,
        source,
    };
    // console.log("fetchUserData", body);
    const data = yield apiClient.post("/users/auth", body);
    if (!data || !data.user || !data.authToken)
        return { user: null, authToken: "" };
    const user = new User(data.user);
    console.log("fetchUserData", user);
    const authToken = String(data.authToken);
    apiClient.session = user.session;
    Sentry.setUser({
        id: user.id,
        authId,
        email,
        address,
        network,
    });
    return { user, authToken };
});
export const signInByAuthToken = (token) => __awaiter(void 0, void 0, void 0, function* () {
    Sentry.addBreadcrumb({
        category: "signInByAuthToken",
        level: "info",
    });
    if (!token)
        return null;
    console.log("signInByAuthToken", token);
    try {
        useUserStore.setState({
            authenticating: true,
        });
        console.log("signInByAuthToken", token);
        const data = yield apiClient.get(`/users/auth`, {
            token,
        });
        if (!data || !data.user || !data.authToken)
            throw AuthError.INVALID_AUTH_TOKEN;
        const { user: userParams, authToken } = data;
        const user = new User(userParams);
        apiClient.session = user.session;
        Sentry.setUser({
            id: user.id,
            email: user.email,
        });
        console.log("signInByAuthToken", authToken, user);
        setAuthStates(user, authToken);
        return user;
    }
    catch (error) {
        console.error("signInByAuthToken", error);
        logOut();
        return null;
    }
});
export const signInByWallet = (wallet) => __awaiter(void 0, void 0, void 0, function* () {
    Sentry.addBreadcrumb({
        category: "signInByWallet",
        level: "info",
    });
    Sentry.setTag("wallet.network", wallet.network);
    Sentry.setTag("wallet.address", wallet.address);
    console.log("signInByWallet", wallet);
    const { user, authToken } = yield fetchUserData("", "", wallet.address, wallet.network);
    Sentry.setUser({
        id: user === null || user === void 0 ? void 0 : user.id,
        email: user === null || user === void 0 ? void 0 : user.email,
        wallet: wallet.address,
    });
    setAuthStates(user, authToken);
    return user;
});
export const logOut = () => __awaiter(void 0, void 0, void 0, function* () {
    window.localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
    const pn = initParticleNetwork();
    console.log(pn.auth.isLogin(), firebaseAuth.currentUser);
    if (pn.auth.isLogin())
        yield to(pn.auth.logout(true));
    if (firebaseAuth.currentUser)
        yield to(signOut(firebaseAuth));
    window.location.reload();
});
